<template>
    <div class="container px-1 py-5">
        <div class="flex flex-col items-start justify-between mb-10 md:flex-row md:items-center">
            <div class="flex flex-col mb-5 md:mb-0 md:flex-row md:items-center w-full">
                <h1 class="lg:text-3xl mb-5 md:mb-0 mr-10 text-2xl">
                    Teljesítési igazolások
                </h1>
                <c-control-text
                    class="grow-0 mb-5 md:max-w-[300px] md:mb-0 md:mr-10"
                    icon="search"
                    name="search"
                    v-bind:placeholder="__('Search')"
                    v-model="s"
                    v-on:keyup="search"
                />

                <div class="flex items-center justify-between mb-5 md:mb-0">
                    <div class="relative">
                        <a class="cursor-pointer duration-500 flex hover:decoration-dotted hover:underline items-center p-1 rounded text-sm transition-all"
                           v-bind:class="{ 'bg-highlight text-white' : Object.keys(filters).length }"
                           v-on:click="openFilters = true"
                        >
                            <c-icon class="h-5 mr-2 stroke-slate-800 w-5"
                                    icon="filter"
                                    v-bind:class="{ '!stroke-white' : Object.keys(filters).length }"
                            />
                            {{ __('Filter results') }}
                            <span class="bg-white flex font-bold h-4 items-center justify-center ml-1 rounded-full text-center text-highlight text-xs w-4"
                                  v-if="Object.keys(filters).length"
                            >
                                {{ Object.keys(filters).length }}
                            </span>
                        </a>

                        <div class="absolute bg-white border border-slate-200 hidden md:w-[500px] mt-1 rounded shadow-lg w-[calc(100vw-0.5rem)] z-30"
                             v-bind:class="{ '!block' : openFilters }"
                             v-on-click-outside="closeFilters"
                        >
                            <div class="flex">
                                <nav class="bg-slate-50 border-r border-slate-200 flex flex-col shrink-0 text-sm">
                                    <a class="cursor-pointer px-2 py-4"
                                       v-bind:class="{ 'after:absolute after:border after:border-[7px] after:border-transparent after:border-l-white after:content-[\'\'] after:h-0 after:left-full after:top-1/2 after:-translate-y-1/2 after:w-0 before:absolute before:border before:border-[8px] before:border-transparent before:border-l-slate-200 before:content-[\'\'] before:h-0 before:left-full before:top-1/2 before:-translate-y-1/2 before:w-0 !bg-white relative' : openFilter === 'created_at' }"
                                       v-on:click="openFilter = 'created_at'"
                                    >
                                        TIG létrehozásának dátuma
                                    </a>
                                </nav>
                                <div class="flex-col grow hidden px-5 py-2"
                                     v-bind:class="{ '!flex' : openFilter === 'created_at' }"
                                >
                                    <div class="mb-5">
                                        <c-control-radio grid="md:grid-cols-2"
                                                         name="filters.created_at.speeddial"
                                                         v-bind:options="filtersSpeedial.options"
                                                         v-model="filtersSpeedial.created_at"
                                                         v-on:change="updateDateFilter('created_at')"
                                        />
                                    </div>
                                    <c-control-date icon="calendar"
                                                    icon-position="right"
                                                    name="filters.created_at"
                                                    multi-calendars
                                                    range
                                                    v-bind:formats="{ input: localizationStore.dateFormat }"
                                                    v-bind:time-config="{ enableTimePicker: false }"
                                                    v-model="createdAt"
                                    />
                                </div>
                            </div>
                            <div class="border-t border-slate-200 flex justify-end p-2">
                                <c-button class="hover:decoration-dotted hover:underline mr-2 p-1.5 text-error" v-on:click="resetFilters">
                                    {{ __('Reset filters') }}
                                </c-button>
                                <c-button class="bg-highlight p-1.5 shrink-0 text-white" v-on:click="applyFilters">
                                    {{ __('Apply filters') }}
                                </c-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex items-center shrink-0">
                <router-link
                    class="bg-highlight inline-flex items-center justify-center px-4 py-2 rounded text-white text-sm"
                    v-bind:to="{ name : 'electronholding.reports.tig.create' }"
                >
                    + TIG létrehozása
                </router-link>
            </div>
        </div>

        <div class="container px-1">
            <c-data-table
                responsive-breakpoint="lg"
                v-bind:data="tableData"
                v-if="tableData.items.length > 0"
            >
                <template v-slot:item.name="{ item }">
                    <a class="text-highlight hover:decoration-dotted hover:underline"
                       target="_blank"
                       v-bind:href="`/tenants/${item.values.path}`"
                    >
                        {{ item.values.name }}
                    </a>
                </template>
                <template v-slot:item.created_at="{ item }">
                    {{ DateTime.fromISO(
                        item.values.created_at,
                        { locale : localizationStore.locale }
                    ).toFormat(localizationStore.dateFormat) }}
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="flex items-center justify-center">
                        <a class="cursor-pointer hover:decoration-dotted hover:underline mr-5 text-highlight text-xs"
                           target="_blank"
                           v-bind:href="`/tenants/${item.values.path}`"
                        >
                            {{ __('Download') }}
                        </a>
                        <a class="cursor-pointer hover:decoration-dotted hover:underline text-red-500 text-xs"
                           v-on:click="deletedTig = item.values.path"
                        >
                            {{ __('Delete') }}
                        </a>
                    </div>
                </template>
            </c-data-table>

            <c-pagination class="my-10"
                          v-bind:current-page="pagesCurrent"
                          v-bind:pages="pagesTotal"
                          v-if="tableData.items.length > 0"
                          v-on:updatePage="updatePage"
            />

            <div class="flex flex-col items-center justify-center text-center" v-else>
                <img class="mb-5"
                     src="/img/empty-state.png"
                     srcset="/img/empty-state.png 2x"
                     v-bind:alt="__('There are no results available.')"
                />
                <div class="flex flex-col items-center justify-center text-center">
                    <span class="font-semibold mb-2 text-center text-lg text-slate-500">
                        {{ __('There are no results available.') }}
                    </span>
                    <router-link
                        class="mt-4 bg-highlight inline-flex items-center justify-center px-4 py-2 rounded text-white text-sm"
                        v-bind:to="{ name : 'electronholding.reports.tig.create' }"
                    >
                        + TIG létrehozása
                    </router-link>
                </div>
            </div>
        </div>
    </div>

    <c-modal class="md:max-w-[450px]" title="TIG törlése" v-bind:open="deletedTig !== null" v-on:closeModal="deletedTig = null">
        <template v-slot:body>
            <div class="py-5 text-center text-sm">
                Biztosan törölni szeretnéd ezt a teljesítési igazolást?
            </div>
        </template>
        <template v-slot:footer>
            <div class="flex justify-end w-full">
                <c-button class="bg-slate-100 mr-2 text-slate-800" v-on:click="deletedTig = null">
                    {{ __('Cancel') }}
                </c-button>
                <c-button class="bg-error text-white" v-on:click="del">
                    {{ __('Delete') }}
                </c-button>
            </div>
        </template>
    </c-modal>
</template>

<script setup lang="ts">
    import { inject, onMounted, ref } from 'vue';
    import { vOnClickOutside } from '@vueuse/components';
    import debounce from 'debounce';
    import { DateTime } from 'luxon';
    import {
        CButton,
        CControlDate,
        CControlRadio,
        CControlText,
        CDataTable,
        CIcon,
        CModal,
        CPagination,
    } from '@teamfurther/cinderblock';
    import { useLocalizationStore } from '../../../../stores/localization';
    import { useRequestStore } from '../../../../stores/request';

    const __ = inject('__');
    const createdAt = ref<any[]>([]);
    const deletedTig = ref<string | null>(null);

    const closeFilters = [
        (e) => {
            if (!e.target.className?.toString().startsWith('dp__')) {
                openFilters.value = false;
            }
        },
    ];

    const filters = ref<any>({});
    const filtersSpeedial = ref<object>({
        created_at : 'anytime',
        options: [
            { label : __('Today'), value : 'today' },
            { label : __('Yesterday'), value : 'yesterday' },
            { label : __('This week'), value : 'this_week' },
            { label : __('Last week'), value : 'last_week' },
            { label : __('This month'), value : 'this_month' },
            { label : __('Last month'), value : 'last_month' },
            { label : __('This year'), value : 'this_year' },
            { label : __('Last year'), value : 'last_year' },
            { label : __('Anytime'), value : 'anytime' },
        ],
    });
    const localizationStore = useLocalizationStore();
    const openFilter = ref<string>('created_at');
    const openFilters = ref<boolean>(false);
    const pagesCurrent = ref(1);
    const pagesTotal = ref(1);
    const requestStore = useRequestStore();
    const s = ref<string>('');

    const search = debounce(async () => {
        if (s.value) {
            filters.value.search = s.value;
        } else {
            delete filters.value.search;
        }

        pagesCurrent.value = 1;
        await getResults();
    }, 500);

    const tableData = ref<any>({
        columnAligns: { actions: 'center' },
        columnNames: {
            order: '#',
            name: __('Name'),
            created_at: __('Created'),
            actions: __('Actions'),
        },
        defaultSort: '+created_at',
        items: [],
        sortableColumns: [],
    });

    async function del() {
        await requestStore.destroy('t/electronholding/tig', { data: { path: deletedTig.value } });

        await getResults();

        deletedTig.value = null;

        requestStore.message = 'TIG sikeresen törölve';
        requestStore.status = 200;
        requestStore.title = (__ as any)('Success');
    }

    async function applyFilters() {
        if (createdAt.value && createdAt.value.length === 2) {
            filters.value.created_at = true;
        } else {
            delete filters.value.created_at;
        }

        pagesCurrent.value = 1;
        await getResults();
        openFilters.value = false;
    }

    async function getResults() {
        let url = 't/electronholding/tig?paginate=1&page=' + pagesCurrent.value;

        if (filters.value.search) {
            url += '&filter[search]=' + encodeURIComponent(filters.value.search);
        }

        if (createdAt.value && createdAt.value.length === 2) {
            const start = DateTime.fromJSDate(new Date(createdAt.value[0])).toISODate();
            const end = DateTime.fromJSDate(new Date(createdAt.value[1])).toISODate();
            url += '&filter[created_at]=' + encodeURIComponent(start + '---' + end);
        }

        const result = await requestStore.get(url);

        pagesCurrent.value = result.data.current_page;
        pagesTotal.value = result.data.last_page;

        const perPage = result.data.per_page;
        const offset = (result.data.current_page - 1) * perPage;

        tableData.value.items = result.data.data.map((tig: any, index: number) => {
            return { values : { order: offset + index + 1, ...tig } };
        });
    }

    async function resetFilters() {
        createdAt.value = [];
        filters.value = {};
        filtersSpeedial.value.created_at = 'anytime';
        
        openFilters.value = false;
        
        await getResults();
    }

    function updateDateFilter(field : string) {
        const dates = (() => {
            switch (filtersSpeedial.value[field]) {
                case 'today':
                    return [
                        DateTime.now(),
                        DateTime.now(),
                    ];
                case 'yesterday':
                    return [
                        DateTime.now().minus({ days: 1 }),
                        DateTime.now().minus({ days: 1 }),
                    ];
                case 'this_week':
                    return [
                        DateTime.now().startOf('week'),
                        DateTime.now(),
                    ];
                case 'last_week':
                    return [
                        DateTime.now().startOf('week').minus({ days: 7 }),
                        DateTime.now().startOf('week').minus({ days: 1 }),
                    ];
                case 'this_month':
                    return [
                        DateTime.now().startOf('month'),
                        DateTime.now(),
                    ];
                case 'last_month':
                    return [
                        DateTime.now().startOf('month').minus({ month: 1 }),
                        DateTime.now().startOf('month').minus({ days: 1 }),
                    ];
                case 'this_year':
                    return [
                        DateTime.now().startOf('year'),
                        DateTime.now(),
                    ];
                case 'last_year':
                    return [
                        DateTime.now().startOf('year').minus({ year: 1 }),
                        DateTime.now().startOf('year').minus({ days: 1 }),
                    ];
            }
        })();

        if (dates) {
            createdAt.value = dates;
        } else {
            createdAt.value = [];
        }
    }

    async function updatePage(page: number) {
        pagesCurrent.value = page;

        await getResults();
    }

    onMounted(async () => {
        await getResults();
    });
</script>

